import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/api/http';
import { AdminPageHeader } from '@/components/admin/admin-shell';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/admin/funcionalidades')({ component: AdminFlags });

const FLAG_META: Record<string, { label: string; description: string }> = {
  recurring_schedules: {
    label: 'Agendamentos recorrentes',
    description: 'Permite que profissionais criem séries semanais ou mensais para o mesmo cliente, com pagamento por sessão ou pré-pago em lote.',
  },
  group_sessions: {
    label: 'Sessões em grupo',
    description: 'Permite que profissionais criem sessões em grupo e que clientes se inscrevam.',
  },
  pix_payouts: {
    label: 'Repasse PIX automático',
    description: 'Dispara a transferência PIX para o profissional automaticamente no dia 1 após NF aprovada (B-04).',
  },
  enotas_integration: {
    label: 'Integração eNotas',
    description: 'Quando ativo, gera NFSe automaticamente; quando desativado, cai no fluxo de upload manual da nota.',
  },
  meet_validation: {
    label: 'Validação automática Meet',
    description: 'Cron consulta Meet API para validar presença e duração da sessão antes de marcar DONE (V-01..V-05).',
  },
};

const SETTING_META: Record<string, {
  label: string; description: string; suffix?: string; min?: number; max?: number; defaultValue: string;
}> = {
  meet_min_duration_pct: {
    label: 'Duração mínima da chamada (%)',
    description: 'Percentual mínimo da duração prevista que a chamada Google Meet precisa ter para ser aprovada automaticamente.',
    suffix: '%', min: 0, max: 100, defaultValue: '80',
  },
};

type Flag = { key: string; enabled: boolean; updatedAt?: string };
type Setting = { key: string; value: string; updatedAt: string };
type Plan = {
  tier: 'FREE' | 'PRO' | 'PREMIUM';
  name: string;
  pricePerMonth: number;
  platformFeePercent: number;
};

function AdminFlags() {
  const qc = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [settingError, setSettingError] = useState<string | null>(null);

  const { data: serverFlags = [], isLoading: flagsLoading } = useQuery({
    queryKey: ['admin-flags'],
    queryFn: () => api.get('admin/feature-flags').json<Flag[]>(),
  });
  const { data: settings = [], isLoading: settingsLoading } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: () => api.get('admin/app-settings').json<Setting[]>(),
  });
  // Single source of truth — backend lib/plans.ts via /v1/subscriptions/plans
  const { data: plansMap } = useQuery({
    queryKey: ['public-plans'],
    queryFn: () => api.get('subscriptions/plans').json<Record<string, Plan>>(),
  });
  const plans: Plan[] = plansMap
    ? (['FREE', 'PRO', 'PREMIUM'] as const).map((t) => plansMap[t]).filter(Boolean)
    : [];
  const moneyBR = (v: number) =>
    v === 0 ? 'Grátis' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const flagMap = new Map(serverFlags.map((f) => [f.key, f]));
  const merged = Object.keys(FLAG_META).map((key) => ({
    key,
    enabled: flagMap.get(key)?.enabled ?? false,
    updatedAt: flagMap.get(key)?.updatedAt,
    ...FLAG_META[key],
  }));

  const setFlag = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      api.patch(`admin/feature-flags/${key}`, { json: { enabled } }).json<any>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-flags'] }),
  });

  const setSetting = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      api.patch(`admin/app-settings/${key}`, { json: { value } }).json<any>(),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['admin-settings'] });
      setDrafts((d) => { const next = { ...d }; delete next[vars.key]; return next; });
      setSettingError(null);
    },
    onError: (e: any) => setSettingError(e?.message ?? 'Erro ao salvar'),
  });

  const getValue = (key: string) => {
    const stored = settings.find((s) => s.key === key)?.value;
    return drafts[key] ?? stored ?? SETTING_META[key].defaultValue;
  };

  return (
    <>
      <AdminPageHeader
        title="Funcionalidades"
        subtitle="Ative, desative ou ajuste parâmetros sem precisar de deploy"
      />

      <div className="max-w-3xl space-y-8">
        {/* ── Feature Flags ── */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Funcionalidades</h2>

          {flagsLoading ? (
            <p className="text-slate-500 text-sm">Carregando...</p>
          ) : (
            merged.map((f) => (
              <div key={f.key} className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800">{f.label}</p>
                    <p className="text-sm text-slate-500 mt-1">{f.description}</p>
                    <p className="text-[11px] text-slate-400 mt-2">
                      Status:{' '}
                      <span className={f.enabled ? 'text-teal-600 font-medium' : 'text-slate-500'}>
                        {f.enabled ? 'Ativado' : 'Desativado'}
                      </span>
                      {f.updatedAt && (
                        <> · Atualizado em {new Date(f.updatedAt).toLocaleString('pt-BR', {
                          day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                        })}</>
                      )}
                    </p>
                    <code className="text-[10px] text-slate-400 mt-1 block">{f.key}</code>
                  </div>
                  <button
                    onClick={() => setFlag.mutate({ key: f.key, enabled: !f.enabled })}
                    disabled={setFlag.isPending}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${f.enabled ? 'bg-teal-500' : 'bg-slate-300'}`}
                    role="switch"
                    aria-checked={f.enabled}
                  >
                    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${f.enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                  </button>
                </div>
              </div>
            ))
          )}
        </section>

        {/* ── App Settings ── */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Parâmetros</h2>

          {settingsLoading && <p className="text-slate-500 text-sm">Carregando...</p>}
          {settingError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              Erro ao salvar: {settingError}
            </p>
          )}

          {Object.entries(SETTING_META).map(([key, meta]) => {
            const stored = settings.find((s) => s.key === key);
            const currentValue = getValue(key);
            const hasDraft = drafts[key] !== undefined;
            return (
              <div key={key} className="bg-white rounded-xl border border-slate-100 shadow-sm p-5 space-y-3">
                <div>
                  <p className="font-semibold text-slate-800">{meta.label}</p>
                  <p className="text-sm text-slate-500 mt-0.5">{meta.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative flex items-center">
                    <input
                      type="number"
                      min={meta.min}
                      max={meta.max}
                      value={currentValue}
                      onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                      className="w-28 pr-7 text-right bg-white border border-slate-200 rounded-md h-9 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                    />
                    {meta.suffix && (
                      <span className="absolute right-2.5 text-sm text-slate-400 pointer-events-none">{meta.suffix}</span>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setSetting.mutate({ key, value: drafts[key]! })}
                    disabled={!hasDraft || setSetting.isPending}
                    className="bg-teal-500 hover:bg-teal-600 text-white"
                  >
                    Salvar
                  </Button>
                </div>
                {stored ? (
                  <p className="text-[11px] text-slate-400">
                    Valor no banco: <strong>{stored.value}{meta.suffix}</strong>
                    {' · '}Atualizado em {new Date(stored.updatedAt).toLocaleString('pt-BR', {
                      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                    })}
                  </p>
                ) : (
                  <p className="text-[11px] text-slate-400">
                    Usando valor padrão: <strong>{meta.defaultValue}{meta.suffix}</strong>
                  </p>
                )}
              </div>
            );
          })}
        </section>

        {/* ── Custos por transação ── */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Custos por transação</h2>
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
            <p className="text-xs text-slate-500 mb-3">
              Tarifas dos provedores de pagamento — descontadas antes do repasse.
            </p>
            <div className="divide-y divide-slate-100">
              {[
                {
                  provider: 'Stripe', use: 'Pagamentos via cartão de crédito',
                  fee: '3,99% + R$ 0,39 por transação', dot: 'bg-indigo-500',
                  feeColor: 'bg-indigo-50 border-indigo-200 text-indigo-700',
                  note: 'Tarifas para cartões nacionais; cartões internacionais e parcelamento têm acréscimos.',
                },
                {
                  provider: 'Banco Inter Empresas', use: 'Cobrança e transferência PIX',
                  fee: 'Sem tarifa', dot: 'bg-emerald-500',
                  feeColor: 'bg-emerald-50 border-emerald-200 text-emerald-700',
                  note: 'Cobrança PIX (entrada) e repasse PIX (saída) gratuitos na conta PJ Inter.',
                },
              ].map((it) => (
                <div key={it.provider} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex items-start gap-3">
                    <span className={`mt-1.5 size-2.5 rounded-full shrink-0 ${it.dot}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-800 text-sm">{it.provider}</span>
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${it.feeColor}`}>
                          {it.fee}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{it.use}</p>
                      <p className="text-[11px] text-slate-400 mt-1">{it.note}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Ciclo de faturamento ── */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Ciclo de faturamento</h2>
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
            <div className="grid grid-cols-3 gap-3 text-center">
              {[
                { label: 'Abertura do ciclo', value: 'Dia 24', sub: 'de cada mês' },
                { label: 'Fechamento do ciclo', value: 'Dia 23', sub: 'do mês seguinte' },
                { label: 'Repasse ao profissional', value: 'Dia 1º', sub: 'após NF aprovada' },
              ].map((c) => (
                <div key={c.label} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="text-xs text-slate-400 mb-1">{c.label}</div>
                  <div className="text-lg font-extrabold text-slate-900">{c.value}</div>
                  <div className="text-[11px] text-slate-400">{c.sub}</div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-3">
              O repasse depende de envio e aprovação da NF do período. Apenas sessões <strong>Concluídas</strong> entram no ciclo.
            </p>
          </div>
        </section>

        {/* ── Taxa de intermediação ── */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Planos & taxa de intermediação</h2>
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
            <p className="text-xs text-slate-500 mb-3">
              Configuração vigente. A taxa é descontada sobre cada sessão no momento em que ela é fechada
              (snapshot, B-06) — mesma fonte usada pra cobrança e pra exibição no perfil do profissional.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-2 text-xs font-semibold text-slate-500">Plano</th>
                    <th className="text-right py-2 text-xs font-semibold text-slate-500">Mensalidade</th>
                    <th className="text-right py-2 text-xs font-semibold text-slate-500">Taxa plataforma</th>
                    <th className="text-right py-2 text-xs font-semibold text-slate-500">Profissional recebe</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {plans.length === 0 ? (
                    <tr><td colSpan={4} className="py-4 text-center text-xs text-slate-400">Carregando...</td></tr>
                  ) : plans.map((p) => (
                    <tr key={p.tier}>
                      <td className="py-2.5 font-medium text-slate-700">
                        {p.name}
                        <span className="text-[10px] text-slate-400 ml-1.5 font-mono uppercase">{p.tier}</span>
                      </td>
                      <td className="py-2.5 text-right text-slate-600 tabular-nums">{moneyBR(p.pricePerMonth)}</td>
                      <td className="py-2.5 text-right text-slate-600 tabular-nums">{p.platformFeePercent}%</td>
                      <td className="py-2.5 text-right font-semibold text-teal-700 tabular-nums">{100 - p.platformFeePercent}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-slate-400 mt-3">
              Fonte: <code>backend/src/lib/plans.ts</code> (servido em <code>GET /v1/subscriptions/plans</code>)
            </p>
          </div>
        </section>
      </div>
    </>
  );
}
